import Form from "@/components/Form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { memo } from "react";
import {
  createUser,
  PAYROLL_COMMENT_MAX_LENGTH,
  SYSTEM_ROLE_CODE,
} from "@/schemas/user";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import { Input } from "@/components/ui/input";
import SystemRoleSelect from "@/components/SystemRoleSelect";
import { Checkbox } from "@/components/ui/checkbox";
import TaxStatusSelect from "@/components/TaxStatusSelect";
import { Textarea } from "@/components/ui/textarea";
import SaveButton from "@/components/SaveButton";
import { isRequired } from "@/lib/form";
import { defaultUserValues } from "../defaultValues";
import { useUserForm } from "../hooks";
import { useUsersContext } from "../context";

const schema = createUser.innerType().shape;

const UserCreatorForm = memo(() => {
  const { t } = useTranslation();

  const { handleCreate } = useUsersContext();

  const {
    form,
    showRegisterAsContractorCheckbox,
    showTaxStatusSelect,
    showPayrollCommentField,
  } = useUserForm(createUser, defaultUserValues);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("users.createForm.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form form={form} handleSubmit={handleCreate}>
          <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field
              name="email"
              label={t("users.fields.email")}
              required={isRequired(schema.email)}
              render={({ field }) => <Input className="w-full" {...field} />}
            />
            <Field
              name="first_name"
              label={t("users.fields.firstName")}
              required={isRequired(schema.first_name)}
              render={({ field }) => <Input className="w-full" {...field} />}
            />
            <Field
              name="last_name"
              label={t("users.fields.lastName")}
              required={isRequired(schema.last_name)}
              render={({ field }) => <Input className="w-full" {...field} />}
            />
            <Field
              name="phone_number"
              label={t("users.fields.phoneNumber")}
              required={isRequired(schema.phone_number)}
              render={({ field }) => <Input className="w-full" {...field} />}
            />
            <Field
              name="system_role_id"
              label={t("users.fields.systemRole")}
              required={isRequired(schema.system_role_id)}
              render={({ field }) => (
                <SystemRoleSelect
                  {...field}
                  excludeSystemRoleCodes={SYSTEM_ROLE_CODE.CONTRACTOR}
                />
              )}
            />
            {showRegisterAsContractorCheckbox && (
              <Field
                name="register_as_contractor"
                label={t("users.fields.register_as_contractor")}
                render={({ field }) => (
                  <div className="mt-2">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="border-gray-400"
                    />
                  </div>
                )}
              />
            )}
            {showTaxStatusSelect && (
              <Field
                name="tax_status"
                label={t("users.fields.taxStatus")}
                required
                render={({ field }) => <TaxStatusSelect {...field} />}
              />
            )}
            {showPayrollCommentField && (
              <Field
                name="payroll_comment"
                label={t("users.fields.payroll_comment")}
                render={({ field }) => (
                  <Textarea
                    className="w-full"
                    {...field}
                    placeholder={t("users.placeholders.payroll_comment")}
                    rows={3}
                    maxLength={PAYROLL_COMMENT_MAX_LENGTH}
                  />
                )}
              />
            )}
          </Fieldset>
          <div className="flex justify-end mt-4">
            <SaveButton />
          </div>
        </Form>
      </CardContent>
    </Card>
  );
});

export default UserCreatorForm;
